
import React, { useState, useEffect } from 'react';
import { useUser } from "@/contexts/UserContext";
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import { format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { DollarSign, TrendingUp, TrendingDown, AlertCircle, List, Star, CheckSquare, Clock, FileText, Calendar, TrendingUpIcon } from 'lucide-react';
import { VerifactuDashboard } from '@/components/VerifactuDashboard';

// Helper to format currency
const formatCurrency = (amount: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);

// Helper to get badge color based on relevance
const getRelevanceBadgeVariant = (relevance: string) => {
    if (relevance === 'destacada') return 'destructive';
    if (relevance === 'importante') return 'warning';
    return 'secondary';
};

export default function Dashboard() {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const { fetchWithAuth } = useUser();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetchWithAuth('/dashboard');
                if (!response.ok) throw new Error('Failed to fetch dashboard data');
                const dashboardData = await response.json();
                setData(dashboardData);
            } catch (error: any) {
                console.error('Error fetching dashboard:', error);
                toast.error('Error al cargar el dashboard', { description: error.message });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [fetchWithAuth]);

    if (loading) {
        return <div className="flex items-center justify-center h-full">Cargando panel de control...</div>;
    }

    if (!data) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>No se pudieron cargar los datos del dashboard. Inténtalo de nuevo más tarde.</AlertDescription>
            </Alert>
        );
    }

    const { financialSummary, yearlyEvolution, bestClients, worstClients, allNotes, allTasks, metrics, alerts } = data;

    // Safety checks for new data structure
    const hasMetrics = metrics && metrics.invoiceCounts;
    const hasAlerts = alerts && Array.isArray(alerts.invoicesDueSoon) && Array.isArray(alerts.overdueTasks);

    // Default values if metrics are missing
    const safeMetrics = hasMetrics ? metrics : {
        invoiceCounts: { total: 0, pagada: 0, emitida: 0, vencida: 0, borrador: 0, anulada: 0 },
        currentMonthInvoices: 0,
        pendingPresupuestos: 0,
        oldPresupuestos: 0,
        averageInvoiceValue: 0,
        averageCollectionTime: 0,
    };

    const safeAlerts = hasAlerts ? alerts : {
        invoicesDueSoon: [],
        overdueTasks: [],
    };

    const formattedYearlyEvolution = yearlyEvolution.map((item: any) => ({
        ...item,
        month: format(parse(item.month, 'yyyy-MM', new Date()), 'MMM', { locale: es }),
    }));

    // Prepare pie chart data for invoice status
    const invoiceStatusData = [
        { name: 'Pagadas', value: safeMetrics.invoiceCounts.pagada, color: '#22c55e' },
        { name: 'Emitidas', value: safeMetrics.invoiceCounts.emitida, color: '#3b82f6' },
        { name: 'Vencidas', value: safeMetrics.invoiceCounts.vencida, color: '#ef4444' },
        { name: 'Borradores', value: safeMetrics.invoiceCounts.borrador, color: '#94a3b8' },
    ].filter(item => item.value > 0);

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold tracking-tighter">Dashboard General</h1>
            
            {/* Financial Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Facturación Total (Pagado)</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(financialSummary.totalInvoiced)}</div>
                        <p className="text-xs text-muted-foreground">Total de ingresos recibidos.</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Gastos Totales</CardTitle>
                        <TrendingDown className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(financialSummary.totalExpenses)}</div>
                        <p className="text-xs text-muted-foreground">Total de gastos registrados.</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Balance Neto</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className={`text-2xl font-bold ${financialSummary.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(financialSummary.netBalance)}
                        </div>
                        <p className="text-xs text-muted-foreground">Facturación - Gastos.</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Pendiente de Cobro</CardTitle>
                        <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(financialSummary.overdue + financialSummary.pendingReceivable)}</div>
                        <p className="text-xs text-muted-foreground">Facturas emitidas y vencidas.</p>
                    </CardContent>
                </Card>
            </div>

            {/* Additional Metrics Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Facturas Este Mes</CardTitle>
                        <FileText className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{safeMetrics.currentMonthInvoices}</div>
                        <p className="text-xs text-muted-foreground">De {safeMetrics.invoiceCounts.total} totales.</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Presupuestos Pendientes</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{safeMetrics.pendingPresupuestos}</div>
                        <p className="text-xs text-muted-foreground">
                            {safeMetrics.oldPresupuestos > 0 && `${safeMetrics.oldPresupuestos} con más de 30 días`}
                            {safeMetrics.oldPresupuestos === 0 && 'Sin retraso.'}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Factura Promedio</CardTitle>
                        <TrendingUpIcon className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(safeMetrics.averageInvoiceValue)}</div>
                        <p className="text-xs text-muted-foreground">Valor medio facturas pagadas.</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Tiempo Medio Cobro</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{safeMetrics.averageCollectionTime} días</div>
                        <p className="text-xs text-muted-foreground">Entre emisión y vencimiento.</p>
                    </CardContent>
                </Card>
            </div>

            {/* Alerts Section */}
            {(safeAlerts.invoicesDueSoon.length > 0 || safeAlerts.overdueTasks.length > 0) && (
                <div className="grid gap-4 md:grid-cols-2">
                    {safeAlerts.invoicesDueSoon.length > 0 && (
                        <Alert variant="default" className="border-amber-500">
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                            <AlertTitle>Facturas Próximas a Vencer</AlertTitle>
                            <AlertDescription>
                                <div className="mt-2 space-y-2">
                                    {safeAlerts.invoicesDueSoon.map((invoice: any) => (
                                        <div key={invoice.id} className="flex justify-between text-sm">
                                            <span>{invoice.numero} - {invoice.clienteName}</span>
                                            <span className="font-medium">{invoice.daysUntilDue} día{invoice.daysUntilDue !== 1 ? 's' : ''}</span>
                                        </div>
                                    ))}
                                </div>
                            </AlertDescription>
                        </Alert>
                    )}
                    {safeAlerts.overdueTasks.length > 0 && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Tareas Vencidas</AlertTitle>
                            <AlertDescription>
                                <div className="mt-2 space-y-2">
                                    {safeAlerts.overdueTasks.map((task: any) => (
                                        <div key={task.id} className="flex justify-between text-sm">
                                            <span>{task.title}</span>
                                            <span className="font-medium">{task.daysOverdue} día{task.daysOverdue !== 1 ? 's' : ''} de retraso</span>
                                        </div>
                                    ))}
                                </div>
                            </AlertDescription>
                        </Alert>
                    )}
                </div>
            )}

            {/* Charts Section */}
            <div className="grid gap-6 md:grid-cols-2">
                {/* Invoice Status Pie Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle>Estado de Facturas</CardTitle>
                        <CardDescription>Distribución por estado ({safeMetrics.invoiceCounts.total} totales).</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {invoiceStatusData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={invoiceStatusData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, value }) => `${name}: ${value}`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {invoiceStatusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                                <div className="text-center">
                                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
                                    <p className="text-sm">No hay facturas registradas aún.</p>
                                    <p className="text-xs mt-1">Crea tu primera factura para ver estadísticas.</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Yearly Evolution Chart (moved here) */}
                <Card>
                    <CardHeader>
                        <CardTitle>Evolución Últimos 12 Meses</CardTitle>
                        <CardDescription>Ingresos vs. Gastos.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={formattedYearlyEvolution}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis tickFormatter={(value) => new Intl.NumberFormat('es-ES', { notation: 'compact' }).format(value as number)} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} labelStyle={{ color: '#f3f4f6' }}/>
                                <Legend />
                                <Bar dataKey="revenue" fill="#22c55e" name="Ingresos" />
                                <Bar dataKey="expenses" fill="#ef4444" name="Gastos" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Client Lists */}
            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Top 5 Mejores Clientes</CardTitle>
                        <CardDescription>Por volumen de facturación (facturas pagadas).</CardDescription>
                    </CardHeader>
                    <CardContent>
                         <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Cliente</TableHead>
                                    <TableHead className="text-right">Total Facturado</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {bestClients.map((client: any) => (
                                    <TableRow key={client.clienteId}>
                                        <TableCell className="font-medium">{client.clienteName}</TableCell>
                                        <TableCell className="text-right font-bold text-green-500">{formatCurrency(client.totalBilled)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Top 5 Clientes Morosos</CardTitle>
                        <CardDescription>Por volumen de deuda vencida.</CardDescription>
                    </CardHeader>
                    <CardContent>
                         <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Cliente</TableHead>
                                    <TableHead className="text-right">Deuda Vencida</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {worstClients.map((client: any) => (
                                    <TableRow key={client.clienteId}>
                                        <TableCell className="font-medium">{client.clienteName}</TableCell>
                                        <TableCell className="text-right font-bold text-red-500">{formatCurrency(client.totalOverdue)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            {/* Notes & Tasks */}
             <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center"><Star className="mr-2 h-5 w-5"/>Todas las Notas</CardTitle>
                        <CardDescription>Ordenadas por relevancia descendente ({allNotes?.length || 0} notas).</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                        {allNotes && allNotes.length > 0 ? (
                            allNotes.map((note: any) => (
                                <div key={note.id} className="p-3 bg-muted/50 rounded-lg">
                                    <div className="flex justify-between items-start">
                                        <p className="font-semibold text-sm">{note.title}</p>
                                        <Badge variant={getRelevanceBadgeVariant(note.relevance)}>{note.relevance}</Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate mt-1">{note.content}</p>
                                </div>
                            ))
                        ) : (
                            <div className="flex items-center justify-center h-24 text-muted-foreground">
                                <p className="text-sm">No hay notas registradas.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
                 <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center"><CheckSquare className="mr-2 h-5 w-5"/>Todas las Tareas Pendientes</CardTitle>
                        <CardDescription>Ordenadas por prioridad y fecha ({allTasks?.length || 0} tareas).</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                        {allTasks && allTasks.length > 0 ? (
                            allTasks.map((task: any) => (
                                <div key={task.id} className="p-3 bg-muted/50 rounded-lg">
                                    <div className="flex justify-between items-start">
                                        <p className="font-semibold text-sm">{task.title || task.titulo}</p>
                                        {task.priority && (
                                            <Badge variant={task.priority === 'urgente' ? 'destructive' : task.priority === 'importante' ? 'warning' : 'secondary'}>
                                                {task.priority}
                                            </Badge>
                                        )}
                                    </div>
                                    {task.dueDate && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Vence: {format(new Date(task.dueDate), "PPP", { locale: es })}
                                        </p>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="flex items-center justify-center h-24 text-muted-foreground">
                                <p className="text-sm">No hay tareas pendientes.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Verifactu Compliance Dashboard */}
            <div className="pt-6 border-t">
                <h2 className="text-2xl font-bold tracking-tighter mb-4">Cumplimiento Verifactu</h2>
                <VerifactuDashboard />
            </div>
        </div>
    );
}
